import { Effect, Option, Queue, Schema as S, Stream } from "effect";
import * as Subscription from "foldkit/subscription";
import posthog from "posthog-js";
import { Message } from "./message";
import type { Model } from "./model";

export const A2A_CONTEXT_FLAG = "a2a-context";
type FlagClient = Pick<
	typeof posthog,
	| "onFeatureFlags"
	| "isFeatureEnabled"
	| "get_distinct_id"
	| "reloadFeatureFlags"
>;

/** The PostHog half of the gate, fail-closed like `watchBillingPreview`. */
export const watchA2aContext = (
	client: FlagClient,
	userId: string,
	emit: (enabled: boolean) => void,
) => {
	const unsubscribe = client.onFeatureFlags((_flags, _variants, context) => {
		emit(
			!context?.errorsLoading &&
				client.get_distinct_id() === userId &&
				client.isFeatureEnabled(A2A_CONTEXT_FLAG, { fresh: true }) === true,
		);
	});
	client.reloadFeatureFlags();
	return unsubscribe;
};

export const a2aContextSubscriptions = Subscription.make<Model, Message>()(
	(entry) => ({
		a2aContext: entry(
			{ userId: S.Option(S.String), generation: S.Number },
			{
				modelToDependencies: (model) => ({
					userId:
						model.auth._tag === "SignedIn"
							? Option.some(model.auth.user.id)
							: Option.none(),
					generation: model.sessionGeneration,
				}),
				dependenciesToStream: ({ userId, generation }) =>
					Option.isNone(userId) || !posthog.__loaded
						? Stream.empty
						: Stream.unwrap(
								Effect.gen(function* () {
									const queue = yield* Queue.unbounded<boolean>();
									yield* Effect.acquireRelease(
										Effect.sync(() =>
											watchA2aContext(posthog, userId.value, (enabled) =>
												Queue.offerUnsafe(queue, enabled),
											),
										),
										(unsubscribe) => Effect.sync(unsubscribe),
									);
									return Stream.fromQueue(queue).pipe(
										Stream.map((enabled) =>
											Message.A2aContextChanged({ generation, enabled }),
										),
									);
								}),
							),
			},
		),
	}),
);
